import React, { useEffect, useRef, useState, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { loginRequest, apiRequest, allowedTenantIds } from './config/authConfig';
import { useMsal } from '@azure/msal-react';
import { useQueryClient } from '@tanstack/react-query';
import { useUserSettings } from './hooks/useQueries';
import { useIsMobile } from './hooks/useIsMobile';
import Sidebar from './components/Navigation/Sidebar';
import BottomNav from './components/Navigation/BottomNav';
import Brainstorming from './pages/BrainstormingPage';
import ItemDetailPage from './pages/ItemDetailPage';
import TaskDetailPage from './pages/TaskDetailPage';
import Tasks from './pages/TaskListView';
import Approvals from './pages/ApprovalsListView';
import ApprovalDetailPage from './pages/ApprovalDetailPage';
import Items from './pages/ItemsListView';
import ItemsImportPage from './pages/ItemsImportPage';
import TrashcanPage from './pages/TrashcanPage';
import UserSettings from './pages/UserSettings';
import ReportsPage from './pages/ReportsPage';
import LoadingSpinner from './components/Popup/LoadingSpinner';
import Toast from './components/Popup/Toast';
import FeedbackButton from './components/FeedbackButton';
import AccessDenied from './pages/AccessDenied';
import AccessDeniedActions from './components/AccessDeniedActions';
import ProtectedRoute from './components/ProtectedRoute';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './App.css';

// Lazy load pages to reduce bundle size
const Settings = React.lazy(() => import('./pages/Settings'));
const HelpGuidePage = React.lazy(() => import('./pages/HelpGuidePage'));

/**
 * Component to handle root path redirect with user's default page preference
 */
function RootRedirect() {
    const { data: settings, isLoading } = useUserSettings();
    
    // Show loading while fetching settings
    if (isLoading) {
        return <LoadingSpinner message="Loading..." size={64} />;
    }
    
    // Use user's default page if available, otherwise default to brainstorming
    const defaultPage = settings?.default_page || '/brainstorming';
    return <Navigate to={defaultPage} replace />;
}

/**
 * Authentication wrapper component that handles automatic login redirects
 */
function AuthWrapper({ children }) {
    const { instance, accounts, inProgress } = useMsal();
    const [authInitialized, setAuthInitialized] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [subscriptionError, setSubscriptionError] = useState(false);
    const [deactivatedError, setDeactivatedError] = useState(false);
    const [seatLimitError, setSeatLimitError] = useState({ show: false, limit: null, current: null });
    const [serverAccessError, setServerAccessError] = useState({ show: false, title: '', message: '', canRequestAccess: false, reason: '' });
    const [configError, setConfigError] = useState(false);
    const [authRedirecting, setAuthRedirecting] = useState(false);
    const authRedirectingRef = useRef(false);
    const lastHandledErrorRef = useRef('');
    const queryClient = useQueryClient();

    const activeTenantId = String(accounts?.[0]?.tenantId || accounts?.[0]?.idTokenClaims?.tid || '').toLowerCase();
    const tenantBlocked = accounts.length > 0
        && allowedTenantIds.length > 0
        && (!activeTenantId || !allowedTenantIds.includes(activeTenantId));

    useEffect(() => {
        if (inProgress === 'none' && accounts.length > 0) {
            authRedirectingRef.current = false;
            setAuthRedirecting(false);
        }
    }, [inProgress, accounts.length]);

    // Global error handler for auth/authorization errors
    useEffect(() => {
        const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
            if (event?.type === 'updated' && !subscriptionError && !deactivatedError && !seatLimitError.show && !serverAccessError.show && !configError) {
                const query = event.query;
                const status = query.state.error?.status;
                const errorCode = query.state.error?.code;
                const errorMessage = query.state.error?.message || '';
                const queryKeyRoot = Array.isArray(query.queryKey) && query.queryKey.length > 0
                    ? String(query.queryKey[0] || '').toLowerCase()
                    : '';
                const isStartupAuthQuery = queryKeyRoot === 'tenant' || queryKeyRoot === 'projects' || queryKeyRoot === 'users';
                const errorKey = `${query.queryHash}:${query.state.errorUpdatedAt}:${status || ''}:${errorCode || ''}`;

                if (lastHandledErrorRef.current === errorKey) {
                    return;
                }
                lastHandledErrorRef.current = errorKey;

                if (status === 401 && (errorCode === 'auth_in_progress' || errorCode === 'interaction_in_progress')) {
                    return;
                }

                // An expired session is the common case for a tab left open overnight:
                // the cached refresh token is gone, but the Entra session usually is
                // not, so a redirect re-acquires a token without the user typing
                // anything. Treat it like the no-account case and re-auth in place
                // rather than stranding the user on an error they can only escape by
                // finding the sign-out button.
                const shouldRetryInteractiveLogin = status === 401 && (
                    errorCode === 'no_account'
                    || errorCode === 'interaction_required'
                    || errorCode === 'login_required'
                );

                // Consent is the exception: it may need an administrator, and a
                // redirect would just bounce the user back to the same failure.
                if (status === 401 && errorCode === 'consent_required') {
                    setToast({
                        show: true,
                        message: errorMessage || 'Sign-in succeeded, but additional API consent is required for this environment.',
                        type: 'error'
                    });
                    return;
                }

                if (status === 403 && errorCode === 'tenant_not_allowed') {
                    setServerAccessError({
                        show: true,
                        title: 'Organization Not Authorized',
                        message: errorMessage || 'Your organization is not authorized to use RIIMS yet.',
                        canRequestAccess: true,
                        reason: 'tenant_not_allowed'
                    });
                    return;
                }

                if (status === 403 && errorCode === 'invalid_scope') {
                    setServerAccessError({
                        show: true,
                        title: 'Organization Not Authorized',
                        message: errorMessage || 'Your organization is not authorized for this application.',
                        canRequestAccess: true,
                        reason: 'invalid_scope'
                    });
                    return;
                }

                if (status === 403 && errorCode === 'unsupported_account_type') {
                    setServerAccessError({
                        show: true,
                        title: 'Unsupported Account Type',
                        message: errorMessage || 'Signed-in account type is not supported for this API resource.',
                        canRequestAccess: false,
                        reason: 'unsupported_account_type'
                    });
                    return;
                }

                // If we hit a 401 anywhere while MSAL is idle, trigger a single re-login.
                // This prevents the UI from retry-spinning indefinitely after session expiry.
                if (shouldRetryInteractiveLogin && inProgress === 'none' && !authRedirectingRef.current && !authRedirecting) {
                    authRedirectingRef.current = true;
                    setAuthRedirecting(true);

                    // With the account still cached, ask for the API token directly and
                    // pass the account hint: if the Entra session is alive this round-trips
                    // and returns without prompting at all. Only fall back to a full login
                    // when there is no account to hint with.
                    const cachedAccount = accounts?.[0] || null;
                    const reauth = cachedAccount
                        ? instance.acquireTokenRedirect({ ...apiRequest, account: cachedAccount })
                        : instance.loginRedirect(loginRequest);

                    reauth.catch((error) => {
                        console.error('Re-authentication redirect failed after 401:', error);
                        const redirectErrorCode = String(error?.errorCode || '').toLowerCase();
                        if (redirectErrorCode !== 'interaction_in_progress') {
                            authRedirectingRef.current = false;
                            setAuthRedirecting(false);
                        }
                    });
                    return;
                }

                if (status === 403) {
                    const errorMessage = query.state.error.message || '';
                    const errorDetails = query.state.error.details;
                    const errorLabel = (errorDetails && typeof errorDetails.error === 'string')
                        ? errorDetails.error
                        : '';

                    const isSeatLimitError =
                        errorLabel.includes('Maximum user limit reached') ||
                        errorMessage.includes('Maximum user limit reached');

                    if (isSeatLimitError) {
                        setSeatLimitError({
                            show: true,
                            limit: Number.isFinite(Number(errorDetails?.limit)) ? Number(errorDetails.limit) : null,
                            current: Number.isFinite(Number(errorDetails?.current)) ? Number(errorDetails.current) : null,
                        });
                        return;
                    }

                    // Detect server-side configuration/authorization failures: the token
                    // was valid and signed, but its audience/issuer/claims don't match what
                    // this API expects (e.g. mismatched AZURE_CLIENT_ID / app registration).
                    // These are environment misconfigurations, not user problems, and left
                    // unhandled they retry-loop the UI, so surface a clear admin-facing page.
                    const isConfigError =
                        errorLabel.includes('Invalid token audience') ||
                        errorLabel.includes('Invalid token issuer') ||
                        errorLabel.includes('Invalid token claims') ||
                        errorMessage.includes('Token must be intended for this API') ||
                        errorMessage.includes('Token must be issued by the expected identity provider');

                    if (isConfigError) {
                        setConfigError(true);
                        return;
                    }

                    // Check if it's a deactivated account error
                    const isDeactivatedError =
                        errorMessage.includes('account has been deactivated') || 
                        errorMessage.includes('Account deactivated') ||
                        (errorDetails && errorDetails.error === 'Account deactivated');
                    
                    if (isDeactivatedError) {
                        setDeactivatedError(true);
                        return;
                    }
                    
                    // Check if it's specifically a subscription error
                    const isSubscriptionError = 
                        errorMessage.includes('Subscription is not active') || 
                        errorMessage.includes('Subscription inactive') ||
                        (errorDetails && errorDetails.error === 'Subscription inactive');
                    
                    if (isSubscriptionError) {
                        setSubscriptionError(true);
                    } else if (isStartupAuthQuery) {
                        // A tenant/organization rejection is recoverable by allow-listing
                        // the org, so those denials get a Request Access button.
                        const isTenantDenial =
                            errorLabel.toLowerCase().includes('tenant not authorized') ||
                            errorLabel.toLowerCase().includes('missing tenant claim') ||
                            errorMessage.toLowerCase().includes('organization is not authorized') ||
                            errorMessage.toLowerCase().includes('contact sales to enable your organization');

                        setServerAccessError({
                            show: true,
                            title: isTenantDenial ? 'Organization Not Authorized' : (errorLabel || 'Access Denied'),
                            message: errorMessage || 'Your account is not authorized to access this application.',
                            canRequestAccess: isTenantDenial,
                            reason: isTenantDenial ? 'tenant_not_allowed' : 'access_denied'
                        });
                    } else {
                        setToast({
                            show: true,
                            message: errorMessage || 'Access denied',
                            type: 'error'
                        });
                    }
                }
            }
        });
        return unsubscribe;
    }, [queryClient, subscriptionError, deactivatedError, seatLimitError.show, serverAccessError.show, configError, instance, inProgress, authRedirecting, accounts]);

    const handleCloseToast = () => {
        setToast({ show: false, message: '', type: 'success' });
    };

    useEffect(() => {
        // Wait for MSAL to finish initializing
        if (inProgress === 'none' && !authInitialized) {
            setAuthInitialized(true);

            // If no accounts are logged in, automatically redirect to login
            // MSAL will automatically return to the current URL after authentication
            if (accounts.length === 0) {
                instance.loginRedirect(loginRequest).catch((error) => {
                    console.error('Login redirect failed:', error);
                });
            }
        }
    }, [instance, accounts, inProgress, authInitialized]);

    // Show loading while MSAL is initializing or before auth check
    if (inProgress !== 'none' || !authInitialized) {
        return (
            <LoadingSpinner
                message="Loading..."
                description="Please wait while we initialize the application."
            />
        );
    }

    // If there are no accounts, we need to redirect to login
    if (accounts.length === 0) {
        return (
            <LoadingSpinner
                message="Redirecting to login..."
                description="Please wait while we redirect you to sign in."
            />
        );
    }

    // Re-authenticating an expired session. The account is still cached, so the
    // app would otherwise keep rendering its failed state until the browser
    // navigates away.
    if (authRedirecting) {
        return (
            <LoadingSpinner
                message="Signing you back in..."
                description="Your session expired. Please wait while we reconnect you."
            />
        );
    }

    if (tenantBlocked) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                backgroundColor: '#f8f9fa',
                padding: '20px',
                textAlign: 'center'
            }}>
                <div style={{
                    backgroundColor: 'white',
                    padding: '40px',
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    maxWidth: '500px'
                }}>
                    <i className="bi bi-shield-lock-fill" style={{
                        fontSize: '4rem',
                        color: '#dc3545',
                        marginBottom: '20px'
                    }}></i>
                    <h2 style={{ color: '#dc3545', marginBottom: '16px' }}>
                        Organization Not Authorized
                    </h2>
                    <p style={{ color: '#6c757d', marginBottom: '24px', lineHeight: 1.6 }}>
                        Your organization is not authorized to use RIIMS yet. Request access and we'll
                        review your organization, or switch to an account that already has access.
                    </p>
                    <AccessDeniedActions
                        reason="client-tenant-not-allowlisted"
                        deniedMessage="Organization Not Authorized"
                        secondaryLabel="Switch Account"
                        onSecondary={() => instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin })}
                    />
                </div>
            </div>
        );
    }

    if (serverAccessError.show) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                backgroundColor: '#f8f9fa',
                padding: '20px',
                textAlign: 'center'
            }}>
                <div style={{
                    backgroundColor: 'white',
                    padding: '40px',
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    maxWidth: '560px'
                }}>
                    <i className="bi bi-shield-exclamation" style={{
                        fontSize: '4rem',
                        color: '#dc3545',
                        marginBottom: '20px'
                    }}></i>
                    <h2 style={{ color: '#dc3545', marginBottom: '16px' }}>
                        {serverAccessError.title || 'Access Denied'}
                    </h2>
                    <p style={{ color: '#6c757d', marginBottom: '24px', lineHeight: 1.6 }}>
                        {serverAccessError.message || 'Your account is not authorized to access this application.'}
                        {serverAccessError.canRequestAccess && ' Request access and we’ll review your organization.'}
                    </p>
                    {serverAccessError.canRequestAccess ? (
                        <AccessDeniedActions
                            reason={serverAccessError.reason || 'tenant_not_allowed'}
                            deniedMessage={serverAccessError.message}
                            secondaryLabel="Sign Out"
                            onSecondary={() => instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin })}
                        />
                    ) : (
                        <button
                            onClick={() => instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin })}
                            style={{
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '12px 24px',
                                fontSize: '16px',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#0056b3'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#007bff'}
                        >
                            Sign Out
                        </button>
                    )}
                </div>
            </div>
        );
    }

    // If subscription is inactive, show subscription error page
    if (subscriptionError) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                backgroundColor: '#f8f9fa',
                padding: '20px',
                textAlign: 'center'
            }}>
                <div style={{
                    backgroundColor: 'white',
                    padding: '40px',
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    maxWidth: '500px'
                }}>
                    <i className="bi bi-exclamation-triangle" style={{
                        fontSize: '4rem',
                        color: '#dc3545',
                        marginBottom: '20px'
                    }}></i>
                    <h2 style={{ color: '#dc3545', marginBottom: '16px' }}>
                        Subscription Inactive
                    </h2>
                    <p style={{ color: '#6c757d', marginBottom: '24px', lineHeight: 1.6 }}>
                        Your subscription is not active. Please contact your administrator to reactivate your subscription.
                    </p>
                    <button 
                        onClick={() => window.location.reload()}
                        style={{
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '12px 24px',
                            fontSize: '16px',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#0056b3'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#007bff'}
                    >
                        Reload Page
                    </button>
                </div>
            </div>
        );
    }

    // If account is deactivated, show deactivated account error page
    if (deactivatedError) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                backgroundColor: '#f8f9fa',
                padding: '20px',
                textAlign: 'center'
            }}>
                <div style={{
                    backgroundColor: 'white',
                    padding: '40px',
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    maxWidth: '500px'
                }}>
                    <i className="bi bi-person-x-fill" style={{
                        fontSize: '4rem',
                        color: '#dc3545',
                        marginBottom: '20px'
                    }}></i>
                    <h2 style={{ color: '#dc3545', marginBottom: '16px' }}>
                        Account Deactivated
                    </h2>
                    <p style={{ color: '#6c757d', marginBottom: '24px', lineHeight: 1.6 }}>
                        Your account has been deactivated. Please contact your administrator if this is an error.
                    </p>
                    <button 
                        onClick={() => window.location.reload()}
                        style={{
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '12px 24px',
                            fontSize: '16px',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#0056b3'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#007bff'}
                    >
                        Reload Page
                    </button>
                </div>
            </div>
        );
    }

    // If tenant has no available seats, show a clear full-page error for end users.
    if (seatLimitError.show) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                backgroundColor: '#f8f9fa',
                padding: '20px',
                textAlign: 'center'
            }}>
                <div style={{
                    backgroundColor: 'white',
                    padding: '40px',
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    maxWidth: '560px'
                }}>
                    <i className="bi bi-people-fill" style={{
                        fontSize: '4rem',
                        color: '#dc3545',
                        marginBottom: '20px'
                    }}></i>
                    <h2 style={{ color: '#dc3545', marginBottom: '16px' }}>
                        No Available Seats
                    </h2>
                    <p style={{ color: '#6c757d', marginBottom: '16px', lineHeight: 1.6 }}>
                        Your organization has reached the maximum number of active users for this subscription.
                    </p>
                    {(seatLimitError.limit !== null || seatLimitError.current !== null) && (
                        <p style={{ color: '#495057', marginBottom: '24px', lineHeight: 1.6, fontWeight: 600 }}>
                            {seatLimitError.current !== null && `Active users: ${seatLimitError.current}`}
                            {seatLimitError.current !== null && seatLimitError.limit !== null && ' of '}
                            {seatLimitError.limit !== null && `Seat limit: ${seatLimitError.limit}`}
                        </p>
                    )}
                    <p style={{ color: '#6c757d', marginBottom: '24px', lineHeight: 1.6 }}>
                        Please contact your RIIMS administrator to add seats or deactivate unused accounts.
                    </p>
                    <button
                        onClick={() => instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin })}
                        style={{
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '12px 24px',
                            fontSize: '16px',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#0056b3'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#007bff'}
                    >
                        Sign Out
                    </button>
                </div>
            </div>
        );
    }

    // If the API rejected the token for configuration reasons (audience/issuer/claims
    // mismatch), show an admin-facing error instead of letting the pages retry-loop.
    if (configError) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                backgroundColor: '#f8f9fa',
                padding: '20px',
                textAlign: 'center'
            }}>
                <div style={{
                    backgroundColor: 'white',
                    padding: '40px',
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    maxWidth: '500px'
                }}>
                    <i className="bi bi-gear-wide-connected" style={{
                        fontSize: '4rem',
                        color: '#dc3545',
                        marginBottom: '20px'
                    }}></i>
                    <h2 style={{ color: '#dc3545', marginBottom: '16px' }}>
                        Configuration Error
                    </h2>
                    <p style={{ color: '#6c757d', marginBottom: '24px', lineHeight: 1.6 }}>
                        We couldn't verify your session with the application's identity
                        settings. This is a server configuration issue, not a problem with
                        your account. Please contact your system administrator to ensure the
                        application is configured correctly.
                    </p>
                    <button
                        onClick={() => window.location.reload()}
                        style={{
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '12px 24px',
                            fontSize: '16px',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#0056b3'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#007bff'}
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    // If we are here, then there is an active account, return the content
    return (
        <>
            {children}
            <FeedbackButton />
            <Toast
                show={toast.show}
                message={toast.message}
                type={toast.type}
                onClose={handleCloseToast}
            />
        </>
    );
}


function App() {
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const isMobile = useIsMobile();
    const sidebarWidth = sidebarCollapsed ? 80 : 200; // match sidebar.css

    return (
        <Router>
            <AuthWrapper>
                <div style={{ display: 'flex', minHeight: '100vh' }}>
                    {!isMobile && (
                        <Sidebar
                            collapsed={sidebarCollapsed}
                            onToggle={() => setSidebarCollapsed((prev) => !prev)}
                        />
                    )}
                    <div
                        className="main-content"
                        style={{
                            flex: '1 1 0',
                            minWidth: 0,
                            marginLeft: isMobile ? 0 : sidebarWidth,
                            width: isMobile ? '100%' : `calc(100vw - ${sidebarWidth}px)`,
                        }}
                    >
                        <Routes>
                            <Route path="/" element={<RootRedirect />} />
                            <Route path="/brainstorming" element={<Brainstorming />} />
                            <Route path="/items" element={<Items />} />
                            <Route path="/items/import" element={
                                <ProtectedRoute requiredRole="user">
                                    <ItemsImportPage />
                                </ProtectedRoute>
                            } />
                            <Route path="/items/new" element={
                                <ProtectedRoute requiredRole="user">
                                    <ItemDetailPage isDraft />
                                </ProtectedRoute>
                            } />
                            <Route path="/items/:id" element={<ItemDetailPage />} />
                            <Route path="/my-items" element={<Items />} />
                            <Route path="/tasks" element={<Tasks />} />
                            <Route path="/tasks/:id" element={<TaskDetailPage />} />
                            <Route path="/my-tasks" element={<Tasks />} />
                            <Route path="/reports" element={<ReportsPage />} />
                            <Route path="/help-guide" element={<HelpGuidePage />} />
                            <Route path="/help" element={<Navigate to="/help-guide" replace />} />
                            <Route path="/approvals" element={<Approvals />} />
                            <Route path="/approvals/:id" element={<ApprovalDetailPage />} />
                            <Route path="/trashcan" element={<TrashcanPage />} />
                            <Route path="/403" element={<AccessDenied />} />
                            <Route path="/admin-settings" element={
                                isMobile ? (
                                    <div className="container-fluid" style={{ padding: '16px' }}>
                                        <div className="alert alert-info" role="alert" style={{ margin: 0 }}>
                                            Admin configuration is not currently supported on mobile.
                                        </div>
                                    </div>
                                ) : (
                                    <ProtectedRoute requiredRole="admin">
                                        <Suspense fallback={<LoadingSpinner message="Loading settings..." size={64} />}>
                                            <Settings />
                                        </Suspense>
                                    </ProtectedRoute>
                                )
                            } />
                            <Route path="/user-settings" element={<UserSettings />} />
                        </Routes>
                    </div>
                </div>
                {isMobile && <BottomNav />}
            </AuthWrapper>
        </Router>
    );
}

export default App;
